import React, {FC} from 'react';
import {Tag} from 'api/types/tagTypes';

import {ErrorMessage, PrimaryButton} from 'components';
import {HSpacer} from 'components/Layout/HSpacer';
import {VSpacer} from 'components/Layout/VSpacer';
import {makeBorderClass} from 'components/Form/Input';

import i18n from 'i18next';

import {useForm, RegisterOptions} from 'react-hook-form';

interface Props {
  tags: Tag[];
  onNewTag: (newTag: string) => void;
  onFocus?: () => void;
}

interface Inputs {
  newTag: string;
}

// Validation rules for the new tag input
const newTagValidationRules: RegisterOptions = {
  required: true,
  pattern: /^[A-Z]+[\w_./+\-@#$%&*~]*$/,
  minLength: 1,
  maxLength: 20,
};

/**
 * Renders new tag input field and submit button, as well as
 * all the validation errors.
 */
export const NewTagForm: FC<Props> = props => {
  // Form stuff
  const {register, handleSubmit, errors} = useForm<Inputs>();

  // Call new tag callback if there are no errors
  const handleCreateNewTag = handleSubmit(({newTag}) => {
    props.onNewTag(newTag);
  });

  return (
    <div>
      <div className="flex">
        {/* Main input field */}
        <input
          type="text"
          name="newTag"
          ref={register(newTagValidationRules)}
          placeholder={i18n.t('tagPage.newTagPlaceholder')}
          className={`w-full h-12 px-4 py-2 ${makeBorderClass()}`}
          onFocus={props.onFocus}
        />

        <HSpacer width="1rem" />

        {/* Submit button */}

        <PrimaryButton onClick={handleCreateNewTag} className="w-64">
          {`${i18n.t('tagPage.addNewTagButtonLabel')} +`}
        </PrimaryButton>
      </div>

      {/* Potential errors below the input field */}

      {errors.newTag && <VSpacer height="1rem" />}
      {errors.newTag?.type === 'pattern' && (
        <ErrorMessage>{i18n.t('tagsPage.allowedCharactersError')}</ErrorMessage>
      )}
      {errors.newTag?.type === 'required' && (
        <ErrorMessage>{i18n.t('tagsPage.pleaseEnterNewTag')}</ErrorMessage>
      )}
      {errors.newTag?.type === 'maxLength' && (
        <ErrorMessage>{i18n.t('tagsPage.maximumLengthError')}</ErrorMessage>
      )}
    </div>
  );
};
